export type ComparisonRow = {
  key: string
  label: string
  before: string
  after: string
}

const humanizeToken = (token: string) =>
  token.length === 0 ? token : `${token[0].toUpperCase()}${token.slice(1)}`

export const formatComparisonLabel = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .map(humanizeToken)
    .join(' ')

export const formatComparisonValue = (value: unknown): string => {
  if (value == null || value === '') return 'Empty'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Empty'
    return value.join(', ')
  }

  if (typeof value === 'object') return JSON.stringify(value)

  return String(value)
}

const isDeepEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) return true
  if (left == null || right == null || typeof left !== 'object' || typeof right !== 'object') {
    return false
  }

  if (left instanceof Date || right instanceof Date) {
    return left instanceof Date && right instanceof Date && left.getTime() === right.getTime()
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false
    }

    return left.every((value, index) => isDeepEqual(value, right[index]))
  }

  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)

  if (leftKeys.length !== rightKeys.length) {
    return false
  }

  return leftKeys.every(
    key =>
      Object.prototype.hasOwnProperty.call(right, key) &&
      isDeepEqual((left as Record<string, unknown>)[key], (right as Record<string, unknown>)[key])
  )
}

export const buildComparisonRows = ({
  oldData,
  newData,
  ignoredFields = new Set<string>()
}: {
  oldData: Record<string, unknown>
  newData: Record<string, unknown>
  ignoredFields?: Set<string>
}): ComparisonRow[] => {
  const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)])

  return Array.from(keys)
    .filter(key => !ignoredFields.has(key))
    .filter(key => !isDeepEqual(oldData[key], newData[key]))
    .sort()
    .map(key => ({
      key,
      label: formatComparisonLabel(key),
      before: formatComparisonValue(oldData[key]),
      after: formatComparisonValue(newData[key])
    }))
}
